<template>
  <div class="d-flex flex-column flex-root legacy-design h-100" :style="cssVars">
    <HeaderMobile v-if="isViewportMobileOrTablet" />

    <div class="d-flex flex-row flex-column-fluid page">
      <div
        id="kt_wrapper"
        class="d-flex flex-column flex-row-fluid wrapper bg-gray-100"
        :class="{
          fullheight: isViewportMobile,
          'with-back-link': displayBackToClientWebsiteLink,
        }"
      >
        <Header v-if="!isViewportMobileOrTablet" />

        <Subheader
          v-if="subheaderDisplay && breadcrumbs.length"
          :breadcrumbs="breadcrumbs"
          :bc_title="pageTitle"
          :bc_icon="pageIcon"
        />

        <!-- Content -->

        <div id="content" :class="`content ${subheaderDisplayClass} d-flex flex-column flex-column-fluid`">
          <div class="d-flex flex-column-fluid">
            <div
              :class="{
                'container-fluid': isLayoutContainerFluid && !isViewportMobile,
                container: isViewportMobile || !isLayoutContainerFluid,
                'bg-white pt-0 px-0': isFullscreenMobileSearch,
              }"
            >
              <div class="d-lg-flex flex-row-fluid">
                <div class="content-wrapper flex-row-fluid">
                  <router-view :key="route.meta.routeKeyToPreventReloadMainLayout || getRouteKey" />
                </div>
              </div>
            </div>
          </div>
          <CustomModal
            id="error-modal"
            :isDisplayedFromParent="shouldDisplayErrorModal"
            :texts="errorModal.texts"
            v-model="shouldDisplayErrorModal"
            :withActionButton="false"
            :withCancelButton="true"
            @cancel="navigateAndResetErrorModal"
            @closedByUser="resetErrorModal"
          />
        </div>

        <Footer />
      </div>
    </div>

    <SharedModal />
  </div>
</template>

<script setup>
/* eslint no-unused-vars:off */
import { getCustomMainColor } from "@/core/services/layout.service";
import { ref, computed, watch, onBeforeMount, onMounted } from "vue";
import { removeCirxModuleFromHeaders } from "@/core/services/api.service";
import { storeToRefs } from "pinia";
import { useContainerLayout } from "@/core/composables/containerLayout";
import { useLayoutConfigStore } from "@/core/store/config/configStore";
import { useResponsive } from "@/core/composables/responsive";
import { useRoute, useRouter } from "vue-router";
import CustomModal from "@/view/components/Shared/CustomModal.vue";
import Footer from "@/view/layout/footer/Footer.vue";
import formattingUtils from "@/core/utils/formatting.utils";
import Header from "@/view/layout/header/Header.vue";
import HeaderMobile from "@/view/layout/header/HeaderMobile.vue";
import HtmlClass from "@/core/services/htmlclass.service";
import SharedModal from "@/view/components/Shared/SharedModal.vue";
import Subheader from "@/view/layout/subheader/Subheader.vue";
import useBreadcrumbStore from "@/core/store/config/breadcrumbsStore";
import useErrorStore from "@/core/store/config/errorStore";
import useFullScreenStore from "@/core/store/layout/fullscreenStore";
import useTranslate from "@/core/composables/translate";
import useUserStore from "@/apps/shared/user/store/userStore";

const errorStore = useErrorStore();

const { fullscreenMobileSearch } = storeToRefs(useFullScreenStore());
const { getLayoutConfig } = useLayoutConfigStore();
const { isLayoutContainerFluid, displayBackToClientWebsiteLink } = useContainerLayout();
const { isViewportMobileOrTablet, isViewportMobile } = useResponsive();
const { pageTitle, pageIcon, breadcrumbs } = storeToRefs(useBreadcrumbStore());
const { resetUser } = useUserStore();
const { translate } = useTranslate();

const router = useRouter();
const route = useRoute();

onBeforeMount(() => {
  HtmlClass.init(getLayoutConfig());
});

onMounted(() => {
  document.documentElement.style.setProperty("--vh", `${window.innerHeight / 100}px`);
});

const blockOtherModals = ref(false);
const shouldDisplayErrorModal = ref(false);

const setErrorModal = (type = null, title = "", content = "", cancelLabel = "") => {
  return {
    type,
    texts: {
      title,
      content,
      cancelLabel,
    },
  };
};

const getRouteKey = computed(() => {
  return (
    route.path +
    Object.entries(route.query)
      .map(([key, value]) => `__${key}_${value}`)
      .join("")
  );
});

const errorModal = ref(setErrorModal());

const isFullscreenMobileSearch = computed(() => fullscreenMobileSearch.value);

const subheaderDisplay = computed(() => !!getLayoutConfig("subheader.display"));

const subheaderDisplayClass = computed(() => {
  return getLayoutConfig("subheader.display") ? "cirx-content" : "cirx-no-subheader";
});

const cssVars = computed(() => ({
  "--custom-primary-color": getCustomMainColor(),
  "--custom-primary-color-rgb": formattingUtils.hexToRgb(getCustomMainColor()),
}));

const {
  resetError,
  errorCode,
  errorGoHome,
  errorHideGenericMessage,
  errorMessage,
  errorRedirectToRoute,
  errorReloadPage,
  errorShouldDisplayModal,
} = storeToRefs(errorStore);

watch(errorShouldDisplayModal, (shouldDisplayModal) => {
  if (!shouldDisplayModal) return;

  blockOtherModals.value = true;

  setTimeout(() => {
    blockOtherModals.value = false;
  }, 3000);

  if (errorCode.value === 401) {
    showReconnectModal();
    return;
  }

  showErrorModal();
});

const showErrorModal = () => {
  const cancelLabel = errorGoHome.value ? translate("MODAL.SERVER.HOME_BUTTON") : translate("MODAL.SERVER.OK_BUTTON");

  const message = !errorHideGenericMessage.value
    ? `${translate("MODAL.SERVER.CONTENT")} ${errorMessage.value}`
    : errorMessage.value;

  errorModal.value = setErrorModal("SERVER", translate("MODAL.SERVER.TITLE"), message, cancelLabel);

  shouldDisplayErrorModal.value = true;
};

const showReconnectModal = () => {
  errorModal.value = setErrorModal(
    "RECONNECT",
    translate("MODAL.RECONNECT.TITLE"),
    translate("MODAL.RECONNECT.CONTENT_EXPIRED"),
    translate("MODAL.RECONNECT.BUTTON")
  );

  shouldDisplayErrorModal.value = true;
};

const navigateAndResetErrorModal = () => {
  if (errorModal.value.type === "RECONNECT") {
    resetUser();
    removeCirxModuleFromHeaders();
    router.push("/login");
  } else {
    if (errorGoHome.value) {
      router.push("/");
    } else if (errorReloadPage.value) {
      router.go();
    } else if (errorRedirectToRoute.value) {
      router.push({ name: errorRedirectToRoute.value });
    }
  }

  resetErrorModal();
};

const resetErrorModal = () => {
  resetError();
  errorModal.value = setErrorModal();
  shouldDisplayErrorModal.value = false;
};
</script>

<style lang="scss">
#content {
  scroll-margin-top: 8rem;
}

.fullheight {
  height: calc(var(--vh, 1vh) * 100);
}

.cirx-custom-primary-on-hover:hover {
  color: var(--custom-primary-color) !important;
}

/* --------------------------------
Utility classes to use in templates
-------------------------------- */

.cirx-custom-primary-color {
  color: var(--custom-primary-color) !important;
}

.cirx-custom-primary-color-svg {
  fill: var(--custom-primary-color) !important;
}

.cirx-custom-primary-color-bg {
  background-color: var(--custom-primary-color) !important;
}

.border-primary-custom {
  border: 1px solid var(--custom-primary-color) !important;
}

.cirx-custom-primary-color-border {
  border-color: var(--custom-primary-color) !important;
}

.cirx-custom-primary-color-input-border:focus {
  border-color: var(--custom-primary-color) !important;
}

.cirx-custom-primary-color-input-border:focus {
  border: 1px solid var(--custom-primary-color) !important;
}

.cirx-custom-primary-color-btn {
  // to be used in conjunction with the variant="primary" prop
  border-color: var(--custom-primary-color) !important;
  background-color: var(--custom-primary-color) !important;
}

/* --------------------------------
Customizations
-------------------------------- */

/* -------------------- Outline-primary variant button */

.cirx-custom-primary-color-btn.btn-outline-primary {
  background-color: transparent !important;
  border-color: var(--custom-primary-color) !important;
  color: var(--custom-primary-color) !important;
}

.cirx-custom-primary-color-btn.btn-outline-primary:hover {
  background-color: var(--custom-primary-color) !important;
  color: white !important;
}

/* -------------------- Light-primary variant button */

.cirx-custom-primary-color-btn.btn-light-primary {
  color: var(--custom-primary-color) !important;
  background-color: rgba(var(--custom-primary-color-rgb), 0.3) !important;
  border-color: transparent !important;
}

.cirx-custom-primary-color-btn.btn-light-primary:hover {
  color: #ffffff !important;
  background-color: var(--custom-primary-color) !important;
  border-color: transparent !important;
}

/* -------------------- Navigation menu, topbar and header */

.cirx-menu-link path {
  fill: $cirx-gray-darker !important;
}

.topbar-item .btn.btn-hover-primary:hover:not(.btn-text):not(:disabled):not(.disabled),
.topbar-item .btn.btn-hover-primary:focus:not(.btn-text),
.topbar-item .btn.btn-hover-primary.focus:not(.btn-text) {
  background-color: var(--custom-primary-color) !important;
  border-color: var(--custom-primary-color) !important;
}

/* -------------------- Input custom colors */

.input-focus-custom-primary:focus {
  border: 1px solid var(--custom-primary-color);
}
.input-focus-custom-primary:active {
  border: 1px solid var(--custom-primary-color);
}

/* Svg icons in aside menu and topbar */

.aside-menu .menu-nav > .menu-item:hover > .menu-link .menu-icon.svg-icon svg g [fill] {
  fill: var(--custom-primary-color) !important;
}

.aside-menu .menu-nav > .menu-item.menu-item-open > .menu-link .menu-icon.svg-icon svg g [fill] {
  fill: var(--custom-primary-color) !important;
}

.aside-menu .menu-nav > .menu-item.menu-item-active > .menu-link .menu-icon.svg-icon svg g [fill] {
  fill: var(--custom-primary-color) !important;
}

.quick-toggle-btn:hover .svg-icon svg g [fill],
.svg-icon.svg-icon-primary.cirx-custom-primary-color-svg svg g [fill] {
  fill: var(--custom-primary-color) !important;
}

/* Parent links in aside menu */

.menu-item:hover .cirx-menu-link *,
.menu-item-open .cirx-menu-link *,
.menu-item-active .cirx-menu-link * {
  color: var(--custom-primary-color) !important;
}

/* Child links in aside menu */

a.menu-item:hover *:not(.dark-text),
span.menu-item-active *:not(.dark-text) {
  color: var(--custom-primary-color) !important;
}

a.menu-item:hover > .menu-link .menu-bullet.menu-bullet-dot > span,
a.menu-item > span.menu-item-active.menu-link > .menu-bullet.menu-bullet-dot > span {
  background-color: var(--custom-primary-color) !important;
}

/* -------------------- Dropdown select */

.dropdown-item.active,
.dropdown-item:active {
  color: #ffffff;
  text-decoration: none;
  background-color: var(--custom-primary-color) !important;
}

/* -------------------- Radio buttons */

.cirx-radio-options .custom-control-input:checked ~ .custom-control-label::before,
.custom-radio .custom-control-input:checked ~ .custom-control-label::before,
.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  border-color: var(--custom-primary-color) !important;
  background-color: var(--custom-primary-color) !important;
}

/* -------------------- Tables and pagination */

.cirx-table-header[aria-sort="ascending"],
.cirx-table-header[aria-sort="descending"] {
  color: var(--custom-primary-color);
}

.pagination .page-item:not(.active) a,
.b-pagination .page-item:not(.active) button,
.b-pagination-pills .page-item:not(.active) button {
  color: var(--custom-primary-color);
}

.pagination .page-item.active a,
.b-pagination .page-item.active button,
.b-pagination-pills .page-item.active button {
  border-color: var(--custom-primary-color);
  background-color: var(--custom-primary-color);
}

.toastui-editor-defaultUI .toastui-editor-ok-button:hover {
  background-color: var(--custom-primary-color) !important;
}

.toastui-editor-defaultUI .toastui-editor-ok-button {
  background-color: var(--custom-primary-color) !important;
  border-radius: 0.85rem !important;
}

.toastui-editor-close-button {
  border-radius: 0.85rem !important;
  background-color: var(--secondary) !important;
  float: left;
}

.toastui-editor-popup {
  border-radius: 0.85rem !important;
}

.toastui-editor-popup-body input {
  border-radius: 6px !important;
  border: 1px solid var(--secondary) !important;
}

.toastui-editor-popup-body input[type="text"]:focus {
  outline: 0px solid var(--secondary) !important;
}

.icon-link-after::after {
  font-family: "Font Awesome 5 Free";
  padding-left: 5px;
  font-size: 0.8em;
  font-weight: 600;
  content: "\f35d";
}
</style>
